import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import { ReactNode } from "react";

import { UploadResult } from "../../../models";
import { StatCard } from "../StatCard/StatCard";

interface UploadResultDialogProps<T> extends Pick<DialogProps, "maxWidth"> {
  open: boolean;
  onClose: () => void;
  title?: string;
  uploadResult: UploadResult<T> | null;
  renderRowData?: (rowData: T) => ReactNode;
}

export const UploadResultDialog = <T,>(props: UploadResultDialogProps<T>) => {
  const {
    open,
    onClose,
    title = "Upload Details",
    uploadResult,
    renderRowData,
    maxWidth = "md",
  } = props;

  const defaultRenderRowData = (rowData: T) => (
    <Box
      component="pre"
      sx={{
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        m: 0,
        fontFamily: "Roboto Mono, monospace",
        fontSize: 12,
      }}
    >
      {JSON.stringify(rowData ?? {}, null, 2)}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {uploadResult ? (
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              flexWrap="wrap"
            >
              <StatCard label="Created" value={uploadResult.createdCount} />
              <StatCard label="Updated" value={uploadResult.updatedCount} />
              <StatCard label="Skipped" value={uploadResult.skippedCount} />
              <StatCard
                label="Row Errors"
                value={uploadResult.rowErrors.length}
              />
            </Stack>
            <Divider />
            {uploadResult.rowErrors.length === 0 ? (
              <Alert severity="success">No row-level issues detected.</Alert>
            ) : (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Row Errors ({uploadResult.rowErrors.length})
                </Typography>
                <List
                  dense
                  sx={{
                    maxHeight: 360,
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  {uploadResult.rowErrors.map((rowError, index) => (
                    <ListItem
                      key={`${rowError.rowIndex}-${index}`}
                      alignItems="flex-start"
                    >
                      <ListItemText
                        primary={`Row ${rowError.rowIndex}: ${rowError.reason}`}
                        secondary={
                          renderRowData
                            ? renderRowData(rowError.rowData)
                            : defaultRenderRowData(rowError.rowData)
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No upload results to display yet.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
